import { useQuery } from '@tanstack/react-query';
import { Field, Select, Spinner } from '@wundo/ui';
import { CohortSearchSelect } from '@/components/pickers/CohortPicker.tsx';
import { OrganisationPicker } from '@/components/pickers/OrganisationPicker.tsx';
import { UserPicker } from '@/components/pickers/UserPicker.tsx';
import type { UserOption } from '@/components/pickers/userOption.ts';
import {
  listNotificationRoleplays,
  type NotificationRoleplay,
} from '@/lib/notificationSend.ts';
import type { CohortWithOrganisation } from '@/types/cohorts.ts';
import type { Organisation } from '@/types/organisations.ts';
import type { RecipientSearch, ScopeKind } from './useRecipients.ts';

const SCOPE_OPTIONS = [
  { value: 'user', label: 'Single user' },
  { value: 'organisation', label: 'Organisation' },
  { value: 'cohort', label: 'Cohort' },
];

interface RecipientFinderProps {
  /** Null when the header says "All organisations"; otherwise every search stays inside it. */
  headerOrganisation: Organisation | null;
  search: RecipientSearch;
  /** Null hides the roleplay field: the moment takes none, or the people are not known yet. */
  roleplayLibraryId: string | null;
  roleplayId: string;
  loading: boolean;
  onScopeChange: (scope: ScopeKind) => void;
  onUserPick: (user: UserOption | null) => void;
  onOrganisationChange: (organisation: Organisation | null) => void;
  onCohortChange: (cohort: CohortWithOrganisation | null) => void;
  onRoleplayChange: (roleplayId: string) => void;
}

export function RecipientFinder({
  headerOrganisation,
  search,
  roleplayLibraryId,
  roleplayId,
  loading,
  onScopeChange,
  onUserPick,
  onOrganisationChange,
  onCohortChange,
  onRoleplayChange,
}: RecipientFinderProps) {
  return (
    <>
      <Field label='Find the recipient by' htmlFor='scope'>
        <Select
          id='scope'
          wrapperClassName='w-full'
          className='w-full'
          value={search.scope}
          onChange={(event) => onScopeChange(event.target.value as ScopeKind)}
          options={SCOPE_OPTIONS}
        />
      </Field>

      {search.scope === 'user' && (
        <Field label='User' htmlFor='user'>
          <UserPicker
            id='user'
            organisationId={headerOrganisation?.id}
            value={search.user}
            onChange={onUserPick}
            placeholder='Select a user...'
          />
        </Field>
      )}

      {search.scope === 'organisation' && headerOrganisation === null && (
        <Field label='Organisation' htmlFor='organisation'>
          <OrganisationPicker
            id='organisation'
            value={search.organisation}
            onChange={onOrganisationChange}
            placeholder='Select an organisation...'
            clearable
          />
        </Field>
      )}

      {search.scope === 'cohort' && (
        <Field label='Cohort' htmlFor='cohort'>
          <CohortSearchSelect
            id='cohort'
            organisationId={headerOrganisation?.id}
            value={search.cohort}
            onChange={onCohortChange}
            placeholder='Select a cohort...'
          />
        </Field>
      )}

      {roleplayLibraryId !== null && (
        <RoleplayField
          organisationId={roleplayLibraryId}
          value={roleplayId}
          onChange={onRoleplayChange}
        />
      )}

      {loading && (
        <p role='status' className='flex items-center gap-2 text-sm text-ink-muted'>
          <Spinner className='size-4' />
          Loading people...
        </p>
      )}
    </>
  );
}

function roleplayLabel(roleplay: NotificationRoleplay): string {
  return roleplay.title || roleplay.type_label || 'Untitled';
}

interface RoleplayFieldProps {
  organisationId: string;
  value: string;
  onChange: (roleplayId: string) => void;
}

function RoleplayField({ organisationId, value, onChange }: RoleplayFieldProps) {
  const roleplays = useQuery({
    queryKey: ['notificationRoleplays', organisationId],
    queryFn: () => listNotificationRoleplays(organisationId),
  });

  return (
    <Field
      label='Roleplay'
      required
      htmlFor='roleplay'
      description='A published roleplay from this organisation’s library. Whoever does not have it on their practice page, or already did it, is left out.'
    >
      <Select
        id='roleplay'
        wrapperClassName='w-full'
        className='w-full'
        value={value}
        onChange={(event) => onChange(event.target.value)}
        options={[
          {
            value: '',
            label:
              roleplays.data?.length === 0
                ? 'This organisation has no published roleplays'
                : 'Choose a roleplay...',
          },
          ...(roleplays.data ?? []).map((roleplay) => ({
            value: roleplay.id,
            label: roleplayLabel(roleplay),
          })),
        ]}
      />
    </Field>
  );
}
